/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { TabValue } from "../config";
import { TABS } from "../config";
import { DateRangePicker, type DateRange } from "@lemmonhq/uikit";
import type {
  ReservationStatus as ReservationStatusType,
  ReservationRequestStatus,
} from "@lemmonhq/types";

export type ReservationStatus = ReservationStatusType | "all";
export type RequestStatus = ReservationRequestStatus | "all";

interface PageHeaderProps {
  activeTab: TabValue;
  dateRange: DateRange;
  reservationStatus: ReservationStatus;
  requestStatus: RequestStatus;
  loading: boolean;
  isSearchOpen: boolean;
  searchQuery: string;
  totalCount: number;
  statusCounts?: Record<string, number>;
  isFiltersOpen: boolean;
  onTabChange: (tab: TabValue) => void;
  onDateRangeChange: (range: DateRange) => void;
  onReservationStatusChange: (status: ReservationStatus) => void;
  onRequestStatusChange: (status: RequestStatus) => void;
  onRefresh: () => void;
  onSearchToggle: () => void;
  onSearchChange: (query: string) => void;
  onFiltersToggle: () => void;
}

export const PageHeader = defineComponent<PageHeaderProps>({
  name: "PageHeader",
  props: [
    "activeTab",
    "dateRange",
    "reservationStatus",
    "requestStatus",
    "loading",
    "isSearchOpen",
    "searchQuery",
    "totalCount",
    "statusCounts",
    "isFiltersOpen",
    "onTabChange",
    "onDateRangeChange",
    "onReservationStatusChange",
    "onRequestStatusChange",
    "onRefresh",
    "onSearchToggle",
    "onSearchChange",
    "onFiltersToggle",
  ] as any,
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="w-full bg-base-100">
        {/* Top Bar */}
        <div class="px-4 xl:px-6 py-5 border-b ">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Left: Title */}
            <div class="flex items-center gap-2 w-full">
              <span class="p-0 w-12 h-12 flex justify-center items-center bg-primary/10 rounded-lg">
                <span class="material-symbols-outlined text-2xl text-primary flex-shrink-0">
                  history
                </span>
              </span>
              <div class="flex flex-col flex-1 min-w-0">
                <h1 class="text-lg sm:text-2xl font-bold text-accent truncate">
                  {t("reservations.history.reservation_history")}
                </h1>
                {props.statusCounts && (
                  <div class="flex items-center gap-2 text-[10px] text-base-content/50 flex-wrap">
                    {Object.entries(props.statusCounts)
                      .filter(([, count]) => count > 0)
                      .map(([status, count]) => (
                        <span key={status} class="whitespace-nowrap">
                          {status
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                          <span class="font-bold text-base-content/70">
                            {count}
                          </span>
                        </span>
                      ))}
                  </div>
                )}
              </div>
              <div class="flex self-center gap-1 lg:gap-2 items-center justify-end flex-shrink-0">
                {/* Desktop Filters — inline with buttons */}
                <div
                  class="hidden lg:flex items-center gap-2 mr-2"
                  style="--fallback-select-height: 36px;"
                >
                  <div
                    style="height: 36px;"
                    class="[&_select]:!h-9 [&_select]:!min-h-0 [&_select]:text-sm [&_button]:!h-9 [&_button]:!min-h-0"
                  >
                    <DateRangePicker
                      value={props.dateRange}
                      onChange={props.onDateRangeChange}
                    />
                  </div>
                  <select
                    class="select select-sm select-bordered bg-base-100"
                    style="height: 36px; min-height: 36px;"
                    value={
                      props.activeTab === "reservations"
                        ? props.reservationStatus
                        : props.requestStatus
                    }
                    onChange={(e) => {
                      const value = (e.target as HTMLSelectElement).value;
                      if (props.activeTab === "reservations") {
                        props.onReservationStatusChange(
                          value as ReservationStatus,
                        );
                      } else {
                        props.onRequestStatusChange(value as RequestStatus);
                      }
                    }}
                  >
                    <option value="all">
                      {t("reservations.history.all_statuses")}
                    </option>
                    {props.activeTab === "reservations" ? (
                      <>
                        <option value="confirmed">
                          {t("reservations.status.confirmed")}
                        </option>
                        <option value="changed">Changed</option>
                        <option value="seated">
                          {t("reservations.status.seated")}
                        </option>
                        <option value="completed">
                          {t("reservations.status.completed")}
                        </option>
                        <option value="no-show">
                          {t("reservations.status.no_show")}
                        </option>
                        <option value="cancelled_by_host">
                          {t("reservations.status.cancelled_by_host")}
                        </option>
                        <option value="cancelled_by_user">
                          {t("reservations.status.cancelled_by_user")}
                        </option>
                      </>
                    ) : (
                      <>
                        <option value="pending">
                          {t("reservations.status.pending")}
                        </option>
                        <option value="ignored">
                          {t("reservations.status.ignored")}
                        </option>
                        <option value="confirmed">
                          {t("reservations.status.confirmed")}
                        </option>
                        <option value="rejected">
                          {t("reservations.status.rejected")}
                        </option>
                        <option value="cancelled_by_host">
                          {t("reservations.status.cancelled_by_host")}
                        </option>
                        <option value="cancelled_by_user">
                          {t("reservations.status.cancelled_by_user")}
                        </option>
                      </>
                    )}
                  </select>
                </div>

                <button
                  type="button"
                  class="btn btn-ghost btn-square"
                  style="height: 36px; min-height: 36px; width: 36px;"
                  onClick={props.onSearchToggle}
                  title={t("reservations.history.search")}
                >
                  <span class="material-symbols-outlined text-lg">search</span>
                </button>

                {/* Mobile Filter Toggle */}
                <button
                  type="button"
                  class="btn btn-ghost btn-square lg:hidden"
                  style="height: 36px; min-height: 36px; width: 36px;"
                  onClick={props.onFiltersToggle}
                  title={t("reservations.history.filters")}
                >
                  <span class="material-symbols-outlined text-lg">tune</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar (when open) */}
        {props.isSearchOpen && (
          <div class="flex items-center gap-2 px-4 py-3 border-b border-base-300 bg-base-200/50">
            <div class="relative flex-1">
              <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg">
                search
              </span>
              <input
                type="text"
                placeholder={t("reservations.history.search_placeholder")}
                class="input input-sm bg-base-100 pl-10 pr-4 w-full"
                value={props.searchQuery}
                onInput={(e) =>
                  props.onSearchChange((e.target as HTMLInputElement).value)
                }
                autofocus
              />
            </div>
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-square"
              onClick={props.onSearchToggle}
              title={t("reservations.history.close_search")}
            >
              <span class="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
        )}
        {/* Tabs */}
        <div class="border-b border-base-300">
          <div class="flex gap-1 px-3 xl:px-6">
            {TABS.map((tab) => {
              const isActive = props.activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  class={`px-4 py-3.5 text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap border-b-2 ${
                    isActive
                      ? "text-accent border-accent font-semibold"
                      : "text-base-content/60 border-transparent hover:text-base-content/80"
                  }`}
                  onClick={() => props.onTabChange(tab.value)}
                >
                  <span class="material-symbols-outlined text-base">
                    {tab.icon}
                  </span>
                  <span>{t(tab.labelKey)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  },
});
